import React, { FC, useCallback, useContext, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import { IProductPackageFormPayload, ProductPackageForm } from './ProductPackageForm';
import { useForm } from 'react-hook-form';
import { apiProductPackage } from '../../resources/api/apiProductPackage';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { ProductPackagesFetcher } from '../../fetchers/ProductPackagesFetcher';
import { IProductPlanPackage } from '../../models/ProductPlanPackage';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDialog } from '../../hooks/useDialog';
import { PackagesType } from '../../models/PackagesType';
import { getPackageDurationsState, getPackageVariantsPayload } from '../../models/PackageDuration';

interface IProps {
  pack?: IProductPlanPackage;
  closeDialog: CloseDialogFunc;
  packagesType: PackagesType;
}

const { useEditPackage, useDeletePackage } = apiProductPackage;

export const EditProductPackageDialog: FC<IProps> = ({ pack, closeDialog, packagesType }) => {
  const form = useForm();
  const { onRefresh } = useContext(ProductPackagesFetcher.Context);
  const { execute: editPackage } = useEditPackage(pack?.id);
  const { execute: deletePackage, Guard: GuardDeletePackage } = useDeletePackage(pack?.id);
  const handleError = useApiErrorHandler();

  const { isOpen: isDeleting, openUrl: deleteUrl, close: abortDelete } = useDialog('delete', 'subdialog');

  const { t } = useTranslation();

  const onDelete = useCallback(async () => {
    if (!pack) return;
    try {
      await deletePackage();
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [deletePackage]);

  const onSubmit = useCallback(
    async (payload: IProductPackageFormPayload) => {
      if (!pack) return;
      try {
        const updatedPack = await editPackage({
          name: payload.name,
          tools: payload.tools,
          productId: payload.productId,
          baseDailyPrice: payload.baseDailyPrice,
          isWatermark: payload.isWatermark,
          isAddon: payload.isAddon,
          variants: getPackageVariantsPayload(payload),
        });
        await onRefresh();
        closeDialog(updatedPack.id);
      } catch (e) {
        handleError(e);
      }
    },
    [editPackage, pack]
  );

  useEffect(() => {
    const { durations, customDurationDays, discounts, perpetualTotalPrice } = getPackageDurationsState(
      pack?.variants ?? []
    );

    form.reset({
      name: pack?.name ?? '',
      baseDailyPrice: pack?.baseDailyPrice ?? '',
      productId: pack?.productId ?? '',
      isWatermark: pack?.isWatermark ?? false,
      isAddon: pack?.isAddon ?? packagesType === PackagesType.Addons,
      tools:
        pack?.tools.map((tool) => ({
          quantity: tool.quantity,
          toolId: tool.toolId,
        })) ?? [],
      durations,
      customDurationDays,
      discounts,
      perpetualTotalPrice,
    });
  }, [pack?.id]);

  return (
    <Dialog open={!!pack} onClose={closeDialog} maxWidth="md">
      <DialogTitle>
        {packagesType === PackagesType.Addons
          ? t('adminProducts.addons.editDialog.title', { id: pack?.legacyId ?? pack?.id })
          : t('adminProducts.packages.editDialog.title', { id: pack?.legacyId ?? pack?.id })}
      </DialogTitle>
      <DialogContent>
        {pack?.id ? (
          <Typography variant="body2" color="textSecondary" paragraph>
            {packagesType === PackagesType.Addons
              ? t('adminProducts.addons.editDialog.packageId', { uuid: pack.id })
              : t('adminProducts.packages.editDialog.packageId', { uuid: pack.id })}
          </Typography>
        ) : null}
        <ProductPackageForm form={form} onSubmit={onSubmit} />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        {pack ? (
          <GuardDeletePackage>
            <DangerousButton component={Link} to={deleteUrl} replace={true}>
              {t('common.button.delete')}
            </DangerousButton>
          </GuardDeletePackage>
        ) : null}
        <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
          {packagesType === PackagesType.Addons
            ? t('adminProducts.addons.editDialog.confirm')
            : t('adminProducts.packages.editDialog.confirm')}
        </Button>
      </DialogActions>

      {pack ? (
        <ConfirmationDialog
          open={isDeleting}
          title={
            packagesType === PackagesType.Addons
              ? t('adminProducts.addons.deleteDialog.title')
              : t('adminProducts.packages.deleteDialog.title')
          }
          confirm={onDelete}
          abort={abortDelete}
          message={
            packagesType === PackagesType.Addons
              ? t('adminProducts.addons.deleteDialog.message', { name: pack.name })
              : t('adminProducts.packages.deleteDialog.message', { name: pack.name })
          }
          dangerous
        />
      ) : null}
    </Dialog>
  );
};
